import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { FileText, Pencil, RotateCcw, Eye, X } from 'lucide-react';
import { documentsApi } from '../services/api';
import toast from 'react-hot-toast';

// Editor für die Rechtsdokument-Vorlagen des Accounts (db.document_templates).
// „Eigene" Vorlagen überlagern die neutralen Standard-Generika; Löschen einer
// eigenen Vorlage fällt auf das Generikum zurück (falls es eins gibt).
export default function DocumentTemplateManager({ onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // {type,title,content,description,source}
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      setLoading(true);
      const res = await documentsApi.getTemplates();
      setTemplates(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Laden der Vorlagen');
    } finally {
      setLoading(false);
    }
  }

  async function openEditor(tpl) {
    try {
      const res = await documentsApi.getTemplateDetail(tpl.type);
      setPreview(false);
      setEditing(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Vorlage konnte nicht geladen werden');
    }
  }

  async function handleSave() {
    if (!editing?.title?.trim() || !editing?.content?.trim()) {
      toast.error('Titel und Inhalt dürfen nicht leer sein');
      return;
    }
    try {
      setSaving(true);
      await documentsApi.saveTemplate(editing.type, {
        title: editing.title,
        content: editing.content,
        description: editing.description || '',
      });
      toast.success('Vorlage gespeichert');
      setEditing(null);
      load();
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!resetTarget) return;
    try {
      await documentsApi.deleteTemplate(resetTarget.type);
      toast.success('Eigene Vorlage entfernt');
      setResetTarget(null);
      setEditing(null);
      load();
    } catch (err) {
      console.error(err);
      toast.error('Fehler beim Zurücksetzen');
    }
  }

  const placeholders = [
    'kunde_firma', 'kunde_adresse', 'auftraggeber_firma', 'auftraggeber_adresse',
    'projektname', 'projektbeschreibung', 'ort', 'datum', 'offene_punkte',
    'glaeubiger_id', 'mandatsreferenz',
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">📄 Rechtsdokument-Vorlagen</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Die Vorlagen hinter „Dokumente → Neues Dokument" — NDA, AV-Vertrag, Abnahme &amp; Co.
            Änderungen gelten nur für Ihren Account.
          </p>
        </div>
      </div>
      <p className="text-xs text-slate-400 mb-6">
        Platzhalter werden beim Erstellen automatisch gefüllt:{' '}
        {placeholders.map((p) => (
          <code key={p} className="bg-slate-100 rounded px-1 mx-0.5">{'{{' + p + '}}'}</code>
        ))}
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {templates.map((t) => (
            <Card key={t.type} className="border-slate-200 hover:border-amber-300 transition-colors">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate">{t.title}</div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-slate-400">{t.type}</span>
                    <Badge
                      className={
                        t.source === 'eigene'
                          ? 'bg-amber-100 text-amber-800 border-amber-200 text-[10px]'
                          : 'bg-slate-100 text-slate-500 border-slate-200 text-[10px]'
                      }
                    >
                      {t.source === 'eigene' ? 'Eigene Vorlage' : 'Standard'}
                    </Badge>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-slate-200 hover:border-amber-300 hover:text-amber-700"
                  onClick={() => openEditor(t)}
                >
                  <Pencil className="w-3.5 h-3.5" />
                  Bearbeiten
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Editor-Dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              Vorlage bearbeiten
              {editing?.source === 'eigene' && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">Eigene Vorlage</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Titel</label>
                <Input
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-slate-700">
                    Inhalt (HTML, mit {'{{platzhaltern}}'})
                  </label>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="gap-1.5 text-slate-500"
                    onClick={() => setPreview(!preview)}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    {preview ? 'Code anzeigen' : 'Vorschau'}
                  </Button>
                </div>
                {preview ? (
                  <div
                    className="prose prose-sm prose-slate max-w-none bg-slate-50 rounded-lg border border-slate-200 p-5 max-h-[45vh] overflow-y-auto"
                    dangerouslySetInnerHTML={{ __html: editing.content }}
                  />
                ) : (
                  <Textarea
                    value={editing.content}
                    onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                    rows={18}
                    className="font-mono text-xs leading-relaxed"
                  />
                )}
              </div>
            </div>
          )}
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <div>
              {editing?.source === 'eigene' && (
                <Button
                  variant="ghost"
                  className="gap-1.5 text-slate-500 hover:text-red-600"
                  onClick={() => setResetTarget(editing)}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Eigene Vorlage entfernen
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditing(null)} className="border-slate-200">
                Abbrechen
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                {saving ? 'Speichern…' : 'Speichern'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset-Bestätigung */}
      <Dialog open={!!resetTarget} onOpenChange={(v) => !v && setResetTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Eigene Vorlage entfernen?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">
            Ihre angepasste Vorlage „{resetTarget?.title}" wird gelöscht. Falls es eine
            Standard-Vorlage für diesen Typ gibt, gilt danach wieder diese — sonst
            verschwindet der Typ aus der Auswahl.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)} className="border-slate-200">
              Abbrechen
            </Button>
            <Button onClick={handleReset} className="bg-red-600 hover:bg-red-700 text-white">
              Entfernen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
